// Player Pack Builder (spec 2026-10-07, plan Part G). Edits
// platformConfig/playerPackTemplates — the doc teamstore-createproducts-v4 plans
// and builds packs from — through GET /api/player-packs and
// PUT /api/player-packs/templates (validated server-side by v4's own validator,
// stale copies refused with 409). Margins use the live pricing rules.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { get, put, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { storePath } from '@/lib/store';
import { packMargin, marginRules, packBuildWarnings, type SlotOption } from '@/lib/player-pack-margin';
import {
  editable, GARMENT_COLOR_OPTIONS,
  type GarmentColor, type SlotId, type SlotTemplate, type Templates, type StoredTemplates,
} from '@/lib/player-pack-editable';

type StyleOption = SlotOption & { sourceStyle: string; title: string; configCount: number; unpriced: number };
type StoreRow = {
  id: string;
  name: string;
  status: string;
  choice: 'off' | 'optional' | 'required';
  legacy: boolean;
  unset?: boolean;
  build: string | null;
  error: string | null;
  colors: string;
};
type Payload = {
  templates: StoredTemplates | null;
  styleOptions: Partial<Record<SlotId, StyleOption[]>>;
  stores: StoreRow[];
  truncated?: boolean;
};
type RulesPayload = { success: boolean; rules: Record<string, unknown> };

const SWR_KEY = '/api/player-packs';
const ON_CONFIRM = 'Turn Player Packs on? Every NEW v4 store build will plan Player Packs by default (Optional unless a rep sets Required) and create 7 Shopify products per store (4 pack garments + 3 Player Pack cards). Existing built stores are not changed.';
const SLOT_ORDER: SlotId[] = ['tee', 'shorts', 'hoodie', 'backpack'];
const SLOT_LABEL: Record<SlotId, string> = { tee: 'Tee', shorts: 'Shorts', hoodie: 'Hoodie', backpack: 'Backpack' };
const TOPS = new Set<string>(['tee', 'hoodie']);
const LOCATION_LABEL: Record<string, string> = { FC: 'full-chest logo', LL: 'left-leg logo' };

// The standard library (scripts/seed-platform-config.js, plan Task A4). Off
// (pilot: only stores set to Optional/Required get packs) until the switch is
// turned on.
const SEED: Templates = {
  version: 1,
  enabled: false,
  garmentColor: 'school',
  slots: {
    tee: { sourceStyle: 'ST350', logoLocation: 'FC', includedPersonalization: 'back', extraSpots: ['left-sleeve', 'right-sleeve'] },
    shorts: { sourceStyle: 'ST349', logoLocation: 'LL' },
    hoodie: { sourceStyle: 'LS14001', logoLocation: 'FC', includedPersonalization: 'back', extraSpots: ['left-sleeve', 'right-sleeve'] },
    backpack: { sourceStyle: 'NKDH7709', logoLocation: 'FC' },
  },
  packs: [
    { id: 'pack-1', label: 'Player Pack 1', slots: ['tee', 'shorts'] },
    { id: 'pack-2', label: 'Player Pack 2', slots: ['tee', 'shorts', 'hoodie'] },
    { id: 'pack-3', label: 'Player Pack 3', slots: ['tee', 'shorts', 'hoodie', 'backpack'] },
  ],
};

function money(v: number): string {
  return `$${v.toFixed(2)}`;
}

function optionLabel(o: StyleOption): string {
  return `${o.sourceStyle} — ${o.title} · $${o.basePrice ?? '—'} · cost ${o.itemCost != null ? '$' + o.itemCost.toFixed(2) : 'unknown'}${o.unpriced ? ` · ${o.unpriced} unpriced` : ''}`;
}

function choiceLabel(r: StoreRow): string {
  if (r.legacy) return 'Off (built before packs)';
  if (r.unset && r.choice === 'off') return 'Off (not set)';
  return r.choice === 'off' ? 'Off' : r.choice === 'required' ? 'Required' : 'Optional';
}

function defaultSlot(slot: SlotId): SlotTemplate {
  return JSON.parse(JSON.stringify(SEED.slots[slot]));
}

export default function PlayerPacks() {
  const { data, isLoading, error } = useSWR<Payload>(SWR_KEY, get);
  const { data: rulesData, error: rulesError } = useSWR<RulesPayload>('/api/pricing-rules', get);
  const { rules, missing: rulesMissing } = useMemo(() => marginRules(rulesData?.rules), [rulesData]);

  const [draft, setDraft] = useState<Templates | null>(null);
  // The version and content the draft was seeded from. Save sends THIS rev
  // (not the latest fetched one), so a save over someone else's newer copy 409s
  // instead of silently replacing it.
  const [seededRev, setSeededRev] = useState<string | null | undefined>(undefined);
  const [baseline, setBaseline] = useState('null');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  const dirty = JSON.stringify(draft) !== baseline;
  const latestRev = data ? (data.templates?._rev ?? null) : undefined;

  function seed(p: Payload) {
    const t = editable(p.templates);
    setDraft(t);
    setBaseline(JSON.stringify(t));
    setSeededRev(p.templates?._rev ?? null);
  }

  // Seed once, and again only when the stored doc changed and there is nothing
  // unsaved to lose (focus revalidation must not clobber edits).
  useEffect(() => {
    if (!data) return;
    if (seededRev === undefined || (latestRev !== seededRev && !dirty)) seed(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function edit(fn: (next: Templates) => void) {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as Templates;
      fn(next);
      return next;
    });
  }

  function addPack() {
    edit((t) => {
      const used = new Set(t.packs.map((p) => p.id));
      for (let i = 1; i <= 9; i += 1) {
        if (!used.has(`pack-${i}`)) {
          t.packs.push({ id: `pack-${i}`, label: `Player Pack ${i}`, slots: ['tee', 'shorts'] });
          return;
        }
      }
    });
  }

  function toggleSlot(packIdx: number, slot: SlotId, on: boolean) {
    edit((t) => {
      const cur = new Set(t.packs[packIdx].slots);
      if (on) cur.add(slot); else cur.delete(slot);
      t.packs[packIdx].slots = SLOT_ORDER.filter((s) => cur.has(s));
    });
  }

  async function save() {
    if (!draft || blocked) return;
    const wasOn = (JSON.parse(baseline) as Templates | null)?.enabled === true;
    if (draft.enabled && !wasOn && !confirm(ON_CONFIRM)) return;
    setSaving(true);
    try {
      const out = await put<{ success: boolean; warnings?: string[] }>(`${SWR_KEY}/templates`, { ...draft, _rev: seededRev ?? undefined });
      const fresh = await mutate<Payload>(SWR_KEY);
      if (fresh) seed(fresh);
      if (out?.warnings?.length) toast.warning(`Player Packs saved (off). Pilot stores can't build these yet: ${out.warnings.join('; ')}`);
      else toast.success('Player Packs saved');
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      const body = (e instanceof ApiError ? e.body : null) as { message?: string; error?: string; problems?: string[] } | null;
      if (status === 409) {
        toast.error('Someone else saved first — reload');
        void mutate(SWR_KEY); // pick up their version so the stale banner shows now
      }
      else if (body?.error === 'unbuildable_lineup' && body.problems?.length) {
        toast.error(`Not saved — v4 can't build this lineup: ${body.problems.join('; ')}`);
      } else toast.error(body?.message || body?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const picks = useMemo(() => {
    const out: Record<string, SlotOption | undefined> = {};
    for (const slot of SLOT_ORDER) {
      const style = draft?.slots[slot]?.sourceStyle;
      out[slot] = data?.styleOptions[slot]?.find((o) => o.sourceStyle === style);
    }
    return out;
  }, [draft, data]);

  // Slots a pack uses that v4 can't build. The server refuses such a save while
  // packs are on (400 unbuildable_lineup) and only warns while they are off
  // (then only pilot stores — set Optional/Required — would fail their packs);
  // the page mirrors that so Save is blocked before the round trip.
  const warnings = useMemo(
    () => (draft && data ? packBuildWarnings(draft, data.styleOptions) : []),
    [draft, data],
  );
  const blocked = !!draft?.enabled && warnings.length > 0;

  const stores = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const all = data?.stores || [];
    return q ? all.filter((s) => s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q)) : all;
  }, [data, filter]);

  const inputCls = 'w-full rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]';
  const stale = seededRev !== undefined && latestRev !== undefined && latestRev !== seededRev;

  return (
    <div className="px-4 py-6 md:px-7">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold tracking-tighter">Player Packs</h1>
          <p className="text-[12px] text-muted">
            The pack lineup team stores with packs are built from. Saved to platformConfig/playerPackTemplates; v4 picks up changes within 5 minutes.
          </p>
          {data?.templates?.updatedAt && (
            <p className="mt-0.5 text-[11px] text-subtle">
              Last saved {new Date(data.templates.updatedAt).toLocaleString()}{data.templates.updatedBy ? ` by ${data.templates.updatedBy}` : ''}
            </p>
          )}
        </div>
        {draft && (
          <Button size="sm" disabled={!dirty || saving || blocked} title={blocked ? 'Fix the garments v4 cannot build first' : undefined} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
        )}
      </div>

      {stale && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[11px] text-warning">
          <span>Someone else saved Player Packs after you opened this page. Saving now will be refused.</span>
          <Button size="sm" variant="secondary" onClick={() => data && seed(data)}>Reload their version</Button>
        </div>
      )}

      {error ? (
        <p className="text-[12px] text-danger">Could not load Player Packs.</p>
      ) : isLoading || !data ? (
        <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
      ) : !draft ? (
        <section className="rounded-md border border-border bg-surface px-4 py-6 text-center">
          <h2 className="text-[14px] font-semibold">Player Packs aren't set up yet</h2>
          <p className="mx-auto mt-1 max-w-[460px] text-[12px] text-muted">
            With no template library, no store plans or builds packs. Start from the standard three packs (saved Off: only stores you set to Optional or Required get packs), then edit.
          </p>
          <Button className="mt-3" size="sm" onClick={() => setDraft(JSON.parse(JSON.stringify(SEED)))}>
            Start from the standard 3 packs
          </Button>
        </section>
      ) : (
        <div className="space-y-6">
          <section className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5">
            <button
              type="button"
              role="switch"
              aria-checked={draft.enabled}
              aria-label="Player Packs on by default"
              onClick={() => edit((t) => { t.enabled = !t.enabled; })}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${draft.enabled ? 'bg-success' : 'bg-black/20'}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${draft.enabled ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
            </button>
            <span className="text-[13px] font-semibold">{draft.enabled ? 'On' : 'Off'}</span>
            <div className="min-w-0 flex-1 text-[11px] text-muted">
              <p>Off = only stores you set to Optional or Required get packs (pilot).</p>
              <p>On = every new store gets packs by default (7 Shopify products per store). Stores already built are not changed.</p>
            </div>
            <label className="flex w-full flex-wrap items-center gap-2 border-t border-border pt-2 text-[12px]">
              <span className="font-semibold">Garment colour</span>
              <select
                aria-label="Pack garment colour"
                className="rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]"
                value={draft.garmentColor}
                onChange={(e) => { const v = e.target.value as GarmentColor; edit((t) => { t.garmentColor = v; }); }}
              >
                {GARMENT_COLOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span className="text-[11px] text-muted">Black = every pack garment is black on every store, whatever the school colours.</span>
            </label>
          </section>

          {warnings.length > 0 && (
            <div className={`rounded-md border px-3 py-2 text-[11px] ${blocked ? 'border-danger/40 bg-danger/10 text-danger' : 'border-warning/40 bg-warning/10 text-warning'}`}>
              <p className="font-semibold">
                {blocked
                  ? "Can't save with packs On — v4 can't build these garments:"
                  : 'Packs are off, so this can be saved — but pilot stores (Optional/Required) will fail their packs until these are fixed:'}
              </p>
              <ul className="mt-0.5 list-disc pl-4">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}

          {(rulesError || (rulesData && !rules)) && (
            <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[11px] text-warning">
              {rulesError
                ? "Pricing rules unavailable — margins can't be calculated."
                : `Pricing rules are missing ${rulesMissing.join(', ')} — margins can't be calculated.`}
            </div>
          )}

          <section>
            <h2 className="mb-2 text-[13px] font-semibold">Garments</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {SLOT_ORDER.map((slot) => {
                const tpl = draft.slots[slot];
                const options = data.styleOptions[slot] || [];
                const known = !!tpl && options.some((o) => o.sourceStyle === tpl.sourceStyle);
                return (
                  <div key={slot} className="rounded-md border border-border bg-surface px-3 py-2.5">
                    <div className="mb-1 flex items-baseline justify-between gap-2">
                      <span className="text-[12px] font-semibold">{SLOT_LABEL[slot]}</span>
                      {tpl && (
                        <span className="text-[11px] text-subtle">
                          {LOCATION_LABEL[tpl.logoLocation] || tpl.logoLocation}{TOPS.has(slot) ? ' · name/number on back' : ''}
                        </span>
                      )}
                    </div>
                    {tpl ? (
                      <select
                        aria-label={`${SLOT_LABEL[slot]} style`}
                        className={inputCls}
                        value={tpl.sourceStyle}
                        onChange={(e) => edit((t) => { t.slots[slot]!.sourceStyle = e.target.value; })}
                      >
                        {!known && <option value={tpl.sourceStyle}>{tpl.sourceStyle} — no active catalog config at this print spot</option>}
                        {options.map((o) => <option key={o.sourceStyle} value={o.sourceStyle}>{optionLabel(o)}</option>)}
                      </select>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] text-muted">Not in the library.</span>
                        <Button size="sm" variant="secondary" onClick={() => edit((t) => { t.slots[slot] = defaultSlot(slot); })}>
                          Add {SLOT_LABEL[slot].toLowerCase()}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-[13px] font-semibold">Packs</h2>
              <Button size="sm" variant="secondary" onClick={addPack} disabled={draft.packs.length >= 9}>Add pack</Button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {draft.packs.map((pack, i) => {
                const m = rules ? packMargin(pack.slots, picks, TOPS, rules) : null;
                return (
                  <div key={pack.id} className="rounded-md border border-border bg-surface px-3 py-2.5">
                    <div className="mb-2 flex items-center gap-2">
                      <input
                        aria-label={`${pack.id} label`}
                        className={inputCls}
                        value={pack.label}
                        onChange={(e) => edit((t) => { t.packs[i].label = e.target.value; })}
                      />
                      <span className="shrink-0 font-mono text-[10.5px] text-subtle">{pack.id}</span>
                    </div>
                    <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1">
                      {SLOT_ORDER.map((slot) => (
                        <label key={slot} className={`flex items-center gap-1.5 text-[12px] ${draft.slots[slot] ? '' : 'opacity-45'}`}>
                          <input
                            type="checkbox"
                            checked={pack.slots.includes(slot)}
                            disabled={!draft.slots[slot]}
                            onChange={(e) => toggleSlot(i, slot, e.target.checked)}
                          />
                          {SLOT_LABEL[slot]}
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11.5px]">
                        {!m ? (
                          <span className="text-muted">
                            {rulesError || rulesData ? 'Margin unknown (no usable pricing rules)' : 'Loading pricing rules…'}
                          </span>
                        ) : m.missing.length ? (
                          <span className="text-warning">Profit unknown (no price/cost data for {m.missing.join(', ')})</span>
                        ) : (
                          <>
                            Price {money(m.price!)} · Cost {money(m.cost!)} ·{' '}
                            <span className={m.belowFloor ? 'font-semibold text-danger' : 'font-semibold text-success'}>
                              Profit {money(m.profit!)}{m.belowFloor ? ` (under the ${money(rules!.minimumProfit)} floor)` : ''}
                            </span>
                          </>
                        )}
                      </p>
                      <button
                        type="button"
                        className="rounded-md px-2 py-1 text-[11px] text-danger transition-colors hover:bg-danger/[0.07] disabled:opacity-45"
                        disabled={draft.packs.length <= 1}
                        onClick={() => edit((t) => { t.packs.splice(i, 1); })}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {data && (
        <section className="mt-6">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-[13px] font-semibold">
              Stores <span className="font-normal text-subtle">({data.stores.length})</span>
              {data.truncated && <span className="ml-2 text-[11px] font-normal text-warning">Showing the first {data.stores.length} stores</span>}
            </h2>
            <input
              aria-label="Filter stores by school"
              className="w-full rounded-md border border-border bg-bg px-2 py-1.5 text-[12px] sm:w-56"
              placeholder="Filter by school"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-subtle">
                  <th className="px-3 py-2 font-semibold">School</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Packs</th>
                  <th className="px-3 py-2 font-semibold">Build</th>
                  <th className="px-3 py-2 font-semibold">Colours</th>
                  <th className="px-3 py-2 font-semibold">Error</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((s) => (
                  <tr key={s.id} className="border-t border-border align-top">
                    <td className="px-3 py-2"><Link className="text-primary hover:underline" to={storePath(s.id)}>{s.name}</Link></td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted">{s.status || '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2">{choiceLabel(s)}</td>
                    <td className={`whitespace-nowrap px-3 py-2 ${s.build === 'failed' ? 'text-danger' : s.build === 'built' ? 'text-success' : ''}`}>{s.build || '—'}</td>
                    <td className="px-3 py-2 text-muted">{s.colors || '—'}</td>
                    <td className="px-3 py-2 text-danger">{s.error || ''}</td>
                  </tr>
                ))}
                {!stores.length && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-[12px] text-muted">No stores match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
